'use client';
import { usePathname } from 'next/navigation';
import Header from './Header';
import Footer from './Footer';
import type { SiteStats } from '@/lib/siteStats';
import SmartIntakeBar from './SmartIntakeBar';
import Tracker from './Tracker';
import { QUESTIONNAIRES_OFF } from '@/lib/questionnaireSwitch';
import QuickConsultBar from './QuickConsultBar';
import { QuickConsultProvider } from '@/lib/quickConsultStore';
import { isCategoryPath } from '@/lib/routeKind';
import type { HomeSample } from '@/lib/homeSamples';
import Ct2Site, { ct2KindForPath, ct2PageMountsOwn } from '@/components/ct2/Ct2Site';
import type { Ct2Cat } from '@/lib/ct2Menu';

// ct2 chrome (Founder 15/10/26): every public page. Not here: category/city pages + articles (they mount Ct2Chrome / Ct2Site themselves),
// the multi landing, x3d journeys, short links and system routes.
const CT2_SKIP = /^\/(multi|x3d|x|a|wa|wa-amp|tel|pic|t|seobacklinks|preview-drafts-nb|sitemaps|api|page)(\/|$)|^\/(articles|מאמרים)\/[^/]/;

const PRO_PAGE = /^\/[^/]+\/[^/]+-\d+\/?$/;
// Utility/system pages (2026-07-22, user request) — no lead-gen intent here, the smart
// search doesn't belong. Nginx rewrites the Hebrew paths to the English routes
// internally (see experts.co.il.nginx.ssl.conf) but that doesn't change the browser's
// address bar, so usePathname() reports whichever the visitor actually used — list both.
const UTILITY_PAGES = new Set(['/פרטיות', '/privacy', '/צור-קשר', '/contact', '/תנאים', '/terms', '/אודות', '/about']);

export default function SiteShell({ children, homeSamples = [], ct2Cats = [], siteStats = null }: { children: React.ReactNode; homeSamples?: HomeSample[]; ct2Cats?: Ct2Cat[]; siteStats?: SiteStats | null }) {
  const path = usePathname();
  // /admin and the pro portal (/pro, own chrome — Founder 2026-09-27) render without the site header/footer.
  if (path?.startsWith('/admin') || path?.startsWith('/t/') /* keyed temp view, 05/10/26 */ || path === '/pro' || path?.startsWith('/pro/')) return <>{children}</>;
  const isProPage = PRO_PAGE.test(path ?? '');
  // Category pages (bare /{catSlug} or /{catSlug}/{citySlug}) use the per-category
  // questionnaire instead of the smart-intake bar.
  const isCategoryPage = isCategoryPath(path);
  const isUtilityPage = UTILITY_PAGES.has(path ?? '');
  // multi landing (/multi/<slug>, 02/10/26): the page IS one question — no second search bar above it
  const isMultiPage = !!path?.startsWith('/multi/');
  // home (03/11/26, Founder): the smart search lives in the page body as the hero's primary action — not in a band under the header; the footer is the lean, relevant one
  const isHome = path === '/';
  return (
    <QuickConsultProvider>
      <Tracker />
      {!isCategoryPage && !ct2PageMountsOwn(path) && !CT2_SKIP.test(path ?? '') && <Ct2Site kind={ct2KindForPath(path)} cats={ct2Cats} />}
      <Header />
      {!isProPage && !isCategoryPage && !isUtilityPage && !isMultiPage && !isHome && <div className="ct2-origbar" style={{ display: 'contents' }}><SmartIntakeBar samples={homeSamples} /></div>}
      <main className="min-h-screen">{children}</main>
      <Footer stats={siteStats} lean={isHome} />
      {!QUESTIONNAIRES_OFF && <QuickConsultBar />}
    </QuickConsultProvider>
  );
}
